import React from "react";
import { Box, Flex, Text, Tooltip, VStack } from "@chakra-ui/react";
import { useSearchParams } from "react-router-dom";
import SearchString from "search-string";
import { H2Dashboard } from "components/text/headings";
import { gotoFilteredQuestionList } from "../AIDashboard/AIDashboard";
import { useNavigate } from "react-router-dom";

const StageByGroupBar = ({ groupData }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const handleClick = (questionGroup, stage) => {
    const searchString = SearchString.parse("");
    searchString.addEntry("questionGroup", questionGroup);
    searchString.addEntry("stage", stage);
    const newParams = new URLSearchParams(searchParams);
    newParams.set("q", searchString.toString());
    gotoFilteredQuestionList(navigate, null, null, newParams);
  };

  const rows = React.useMemo(() => {
    if (!Array.isArray(groupData)) return [];

    return groupData
      .map((g) => {
        const groupId = g.groupId ?? g.id ?? g.label;
        const label = g.label ?? g.id;

        const segments = (g.data || [])
          .map((s) => ({
            stage: s.category ?? s.x,
            value: Number(s.value ?? s.y ?? 0),
            color: s.color,
          }))
          .filter((s) => s.stage && s.value > 0);

        const total = segments.reduce((sum, s) => sum + s.value, 0);

        return { groupId, label, segments, total };
      })
      .filter((r) => r.segments.length > 0);
  }, [groupData]);

  const stageOrder = React.useMemo(
    () => rows[0]?.segments.map((s) => s.stage) || [],
    [rows]
  );

  const orderedSegments = (segments) =>
    [...segments].sort(
      (a, b) => stageOrder.indexOf(a.stage) - stageOrder.indexOf(b.stage)
    );

  const maxTotal = React.useMemo(
    () => Math.max(1, ...rows.map((r) => r.total || 0)),
    [rows]
  );

  return (
    <VStack
      spacing={4}
      w="100%"
      p={6}
      display="flex"
      flexDirection="column"
      color="darkTeal"
      align="stretch"
    >
      <H2Dashboard>Stage by group</H2Dashboard>

      <VStack align="stretch" spacing={4} pt={1}>
        {rows.map((row) => (
          <Box key={row.groupId}>
            <Text fontSize="sm" color="gray.600" mb={1}>
              {row.label}
            </Text>

            <Flex align="center" gap={2}>
              <Box
                flex="1"
                h="14px"
                bg="gray.200"
                borderRadius="md"
                overflow="hidden"
              >
              <Flex h="100%" w="100%">
                {orderedSegments(row.segments).map((s) => {
                  const pct = Math.max(
                    0,
                    Math.min(100, (s.value / maxTotal) * 100)
                  );
                  const minW = pct > 0 && pct < 0.8 ? "2px" : undefined;

                  return (
                    <Tooltip
                      key={`${row.groupId}-${s.stage}`}
                      label={`${s.stage}: ${s.value}`}
                      hasArrow={false}
                      placement="top"
                      openDelay={150}
                      bg="white"
                      color="darkTeal"
                      border="1px solid"
                      borderColor="lime"
                      borderRadius="lg"
                      px={3}
                      py={2}
                      fontSize="sm"
                    >
                      <Box
                        w={`${pct}%`}
                        minW={minW}
                        h="100%"
                        bg={s.color}
                        cursor="pointer"
                        onClick={() => handleClick(row.groupId, s.stage)}
                      />
                    </Tooltip>
                  );
                })}
              </Flex>
              </Box>
              <Text fontSize="sm" fontWeight="bold" color="gray.500" minW="30px" textAlign="right">{row.total}</Text>
            </Flex>
          </Box>
        ))}
      </VStack>
    </VStack>
  );
};

export default StageByGroupBar;
